import React, { useEffect, useState } from "react";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Hidden,
  useTheme,
  Breadcrumbs,
  Button,
  makeStyles,
  createStyles,
  Grid,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import HomeIcon from "@material-ui/icons/Home";
import { useForm } from "react-hook-form";

import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_UPDATED,
} from "../../../../../utils/consts";
import {
  fetchGlobalDepartmentInfo,
  fetchProductConfigInfo,
} from "../../../../../services/menuApp/printerMenuService";
import PageHeaderMobile from "../../../../../components/common/PageHeader/PageHeaderMobile";
import DefaultAlert from "../../../../../components/alerts/DefaultAlert";
import ButtonCommon from "../../../../../components/buttons/ButtonCommon";
import withAuthority from "../../../../../components/Auth/withAuthority";
import Authorities from "../../../../../auth/authorities";
import {
  fetchAllMenuDetails,
  fetchMenus,
  setProductsVersion,
} from "../../../../../redux/actions/menu";
import { updateDirectoryInStore } from "../../../../../redux/actions/menu";
import ProductSkeletonLoading from "./ProductSkeletonLoading";

// Import refactored components
import ProductBanner from "./components/ProductBanner";
import OverviewSection from "./components/OverviewSection";
import AvailabilitySection from "./components/AvailabilitySection";
import SystemInformationSection from "./components/SystemInformationSection";
import ActionButtons from "./components/ActionButtons";

// Import hooks and utils
import { useProductData } from "./hooks/useProductData";
import { useProductForm } from "./hooks/useProductForm";
import { useChangeTracking } from "./hooks/useChangeTracking";
import ProductsSection from "./components/ProductsSection";
import { copyToClipboard } from "./types/ProductUtils";
import TagsSpotlightSection from "./components/TagsSpotlightSection";
import { CustomTheme } from "../../../../../types/customTheme";
import {
  updateProductInfo,
  updateWrapperInfo,
} from "../../../../../services/menuApp/menuService";
import { AxiosError } from "axios";
import AvailabilitySections from "./components/AvailabilitySections";
import {
  fetchMenuOption,
  updateMenuOptionInfo,
} from "../../../../../services/menuApp/categoryMenuService";
import { DeliveryTpe } from "../../../../../utils/enum";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: "transparent",
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.background.entity_border}`,
        color: theme.palette.custom.orange.contrastText,
      },
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
    textField: {
      overflowWrap: "break-word",
      wordWrap: "break-word",
      borderRadius: "10px",
      backgroundColor: "transparent",
      [`& fieldset`]: {
        borderRadius: "10px",
        border: `1px solid ${theme.palette.background.entity_border}`,
        cursor: "pointer",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${theme.palette.background.entity_border}`,
      },
      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `1px solid ${theme.palette.background.entity_border}`,
      },
    },
    tagChip: {
      backgroundColor: "#2a215a",
      margin: "4px",
      borderRadius: "8px",
    },
  }),
);

interface Params {
  menuId: string;
  productId: string;
  locationId: string;
  "0"?: string;
}

interface MenuProductProps {
  onProductUpdated?: () => void;
}
// Platform selector object
export const platformSelectorObj: any = {
  "walk in": { id: "walk in", label: "Walk In" },
  phone: { id: "phone", label: "Phone" },
  kiosk: { id: "kiosk", label: "Kiosk" },
  online: { id: "online", label: "Online" },
};

// Channel selector list
export const channelSelectorList = [
  { id: "eatpresto", label: "Eat Presto" },
  { id: "deliverect", label: "Deliverect" },
  { id: "justeat", label: "Just Eat" },
  { id: "deliveroo", label: "Deliveroo" },
  { id: "ubereats", label: "Uber Eats" },
];
const MenuProduct: React.FC<MenuProductProps> = ({ onProductUpdated }) => {
  const params = useParams<Params>();
  const match: any = useRouteMatch();
  const history = useHistory();
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { menuId, productId, locationId } = params;

  // Redux state
  const { menuDetails, menus } = useSelector((state: any) => {
    console.log("asdasd", state);
    return state.menu;
  });
  const currentMenuData = menuDetails[menuId];

  // Local state
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [stockDepartmentSelectedNode, setStockDepartmentSelectedNode] =
    useState<any[]>([]);
  const [itemTypesList] = useState([{ id: "1", label: "1111" }]);
  const [expandedProductIds, setExpandedProductIds] = useState<
    Set<string | number>
  >(new Set());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const productsVersion = useSelector(
    (state: any) => state.menu?.productsVersion || 1,
  );
  const isEditDisabled = productsVersion === 1;
  // Delivery Types State
  const [deliveryTypesListInitial, setDeliveryTypesListInitial] = useState<any>(
    [],
  );
  const [deliveryTypesList, setDeliveryTypesList] = useState<any>([]);

  // Platform State
  const [platformListInitial, setPlatformListInitial] = useState<any>([]);
  const [platformList, setPlatformList] = useState<any>([]);

  // Channel State
  const [channelListInitial, setChannelListInitial] = useState<any>([]);
  const [channelList, setChannelList] = useState<any>([]);

  // Availability State
  const [availabilityList, setAvailabilityList] = useState<any>([]);
  const [availabilityListInitial, setAvailabilityListInitial] = useState<any>(
    [],
  );
  const [availabilityErrorMessage, setAvailabilityErrorMessage] = useState("");

  // State for time and week values
  const [weekFrom, setWeekFrom] = useState<number>(1);
  const [weekTo, setWeekTo] = useState<number>(7);
  const [timeFrom, setTimeFrom] = useState<number>(0);
  const [timeTo, setTimeTo] = useState<number>(23);
  const [hasTimeWeekChanges, setHasTimeWeekChanges] = useState(false);
  const [menuOptionNodeObj, setMenuOptionNodeObj] = useState<any>([]);
  // ✅ NEW: State for full menu option data
  const [menuOptionData, setMenuOptionData] = useState<any>(null);
  const [isMenuOptionLoading, setIsMenuOptionLoading] = useState(true);

  // Active State
  const [originalNodeData, setOriginalNodeData] = useState<any>({
    entityId: "",
    isActive: false,
    availability: [],
    deliveryTypes: [],
  });
  const [isActiveInitial, setIsActiveInitial] = useState(true);
  const [isActive, setIsActive] = useState(true);

  // Custom hooks
  const {
    isValidMainProduct,
    setIsValidMainProduct,
    isValidProduct,
    setIsValidProduct,
    isValidProductAvailability,
    setIsValidProductAvailability,
    isValidKiosk,
    setIsValidKiosk,
    color,
    setColor,
    allYouCanEat,
    setAllYouCanEat,
    isByo,
    setIsByo,
    isCustomisationPriority,
    setIsCustomisationPriority,
    isSpicySelectionEnabled,
    setIsSpicySelectionEnabled,
    inTax,
    setInTax,
    outTax,
    setOutTax,
    selectedTags,
    setSelectedTags,
    spotlightText,
    setSpotlightText,
    selectedItemType,
    setSelectedItemType,
    open,
    setOpen,
    register,
    handleSubmit,
    errors,
    reset,
  } = useProductForm();

  // Custom data hook
  const {
    isLoading,
    productData,
    wrapperData,
    selectedProduct,
    selectedMenuName,
    categoryName,
    categoryId,
    directoryStack,
    selectedDepartmentObj,
    setSelectedDepartmentObj,
    setProductData,
    setWrapperData,
    setSelectedProduct,
  } = useProductData(
    locationId,
    menuId,
    productId,
    stockDepartmentSelectedNode,
    menus,
    currentMenuData,
    reset,
  );

  // Get all platform IDs
  const allPlatformIds = Object.values(platformSelectorObj).map(
    (p: any) => p.id,
  );
  // Get all channel IDs
  const allChannelIds = channelSelectorList.map((c) => c.id);
  // Get all delivery type IDs
  const allDeliveryTypes = [
    DeliveryTpe.DINEIN,
    DeliveryTpe.DELIVERY,
    DeliveryTpe.TAKEOUT,
  ];

  // Initialize states from loaded data
  useEffect(() => {
    if (wrapperData) {
      setIsValidMainProduct(
        wrapperData.valid !== undefined ? wrapperData.valid : true,
      );
      setIsValidKiosk(wrapperData.kiosk?.toString() === "1");
      setColor(wrapperData.colour || "");
      setAllYouCanEat(wrapperData.allYouCanEat || false);
      setIsValidProductAvailability(
        wrapperData.valid !== undefined ? wrapperData.valid : true,
      );

      if (wrapperData.weekFrom !== undefined && wrapperData.weekFrom !== null) {
        setWeekFrom(wrapperData.weekFrom);
      }
      if (wrapperData.weekTo !== undefined && wrapperData.weekTo !== null) {
        setWeekTo(wrapperData.weekTo);
      }
      if (wrapperData.timeFrom !== undefined && wrapperData.timeFrom !== null) {
        setTimeFrom(wrapperData.timeFrom);
      }
      if (wrapperData.timeTo !== undefined && wrapperData.timeTo !== null) {
        setTimeTo(wrapperData.timeTo);
      }

      if (wrapperData.itemType && itemTypesList.length > 0) {
        const foundItemType = itemTypesList.find(
          (item: any) =>
            item.id === wrapperData.itemType ||
            item.label === wrapperData.itemType,
        );
        if (foundItemType) {
          setSelectedItemType(foundItemType);
        }
      }
      if (wrapperData.spotlight !== undefined) {
        setSpotlightText(wrapperData.spotlight || "");
      }
    }

    if (productData) {
      setIsByo(productData.isByo || false);
      setIsCustomisationPriority(productData.isCustomisationPriority || false);
      setIsSpicySelectionEnabled(productData.isSpicySelectionEnabled || false);
      setIsValidProduct(
        productData.valid !== undefined ? productData.valid : true,
      );

      if (productData.inTax !== undefined) {
        setInTax({ id: productData.inTax, name: `${productData.inTax}%` });
      }
      if (productData.outTax !== undefined) {
        setOutTax({ id: productData.outTax, name: `${productData.outTax}%` });
      }
    }
  }, [wrapperData, productData]);
useEffect(() => {
  getMenuOptionInfo();
},[])
  const handleNavigateToCategory = () => {
    if (categoryId) {
      history.push(`/${locationId}/presto-menus/menus/${menuId}`);
    }
  };

  const {
    hasWrapperChanges,
    hasProductChanges,
    trackWrapperChange,
    trackProductChange,
    resetChanges,
    getChangedWrapperData,
    getChangedProductData,
    getAllChangedProducts,
  } = useChangeTracking(wrapperData, selectedProduct);

  const toggleProductExpand = (productId: string | number) => {
    setExpandedProductIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const getProductConfiguration = async () => {
    if (!locationId) return;

    try {
      const { data } = await fetchProductConfigInfo(locationId);
      if (data && data.data) {
        const version = data.data.productsVersion || 1;
        dispatch(setProductsVersion(version));
      }
      
    } catch (error) {
      const err: any = error as AxiosError;
      if (err.response?.status !== 404) {
        console.error("Error fetching product configuration:", error);
      }
    }
  };
useEffect(() => {
  console.log("wrapperData updated:", wrapperData);
  console.log("productData updated:", productData);
}, [wrapperData, productData]);
  // Initial load
  useEffect(() => {
    const loadAllData = async () => {
      if (isInitialLoad && (!menus || menus.length === 0)) {
        console.log("Initial load - fetching all menus and details");
        setIsMenuOptionLoading(false);
    await dispatch(fetchMenus(match.params.locationId));
        await dispatch(fetchAllMenuDetails(match.params.locationId));
        await getProductConfiguration();
        setIsInitialLoad(false);
      }
    };
    loadAllData();
  }, [dispatch, match.params.locationId, menus, isInitialLoad, isEditDisabled]);

  

  // ✅ UPDATED: Get all menu option information
  const getMenuOptionInfo = async () => {
    try {
      const res = await fetchMenuOption(match.params.locationId);
      const menuOptionNode = res.data.data;
      // ✅ Store the full menu option data
      setMenuOptionData(menuOptionNode);
      // Safely get the menu option for this product
      const menuOptionList = menuOptionNode?.options?.[`M${productId}`] || {};
      setMenuOptionNodeObj(menuOptionList);
      setIsMenuOptionLoading(false);
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  // Fetch departments and config
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetchGlobalDepartmentInfo(locationId);
        if (res?.data?.data) {
          setStockDepartmentSelectedNode(res.data.data);
        }
      } catch (err) {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    };

    const fetchConfig = async () => {
      try {
        await fetchProductConfigInfo(locationId);
      } catch (err) {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      }
    };

    fetchDepartments();
    fetchConfig();
  }, [locationId]);

  // Navigation handlers
  const handleBackToDirectory = () => {
    const directoryPath = `${match.url.split("/product")[0]}`;
    history.push(directoryPath);
  };

  const handleNavigateToMenu = () => {
    history.push(`/${locationId}/presto-menus/menus`);
  };

  const handleNavigateToDirectory = (index: number) => {
    let path = `/${locationId}/presto-menus/menus/${menuId}`;
    for (let i = 0; i <= index; i++) {
      path += `/directory/${directoryStack[i].id}`;
    }
    history.push(path);
  };

  const handleWeekFromChange = (value: number) => {
    setWeekFrom(value);
    setHasTimeWeekChanges(true);
    trackWrapperChange("weekFrom", value);
  };

  const handleWeekToChange = (value: number) => {
    setWeekTo(value);
    setHasTimeWeekChanges(true);
    trackWrapperChange("weekTo", value);
  };

  const handleTimeFromChange = (value: number) => {
    setTimeFrom(value);
    setHasTimeWeekChanges(true);
    trackWrapperChange("timeFrom", value);
  };

  const handleTimeToChange = (value: number) => {
    setTimeTo(value);
    setHasTimeWeekChanges(true);
    trackWrapperChange("timeTo", value);
  };

  const saveProductWrapperChanges = async (changedWrapperData: any) => {
    const wrapperBody = {
      id: wrapperData?.id,
      locationId: locationId,
      version: wrapperData?.version,
      ...changedWrapperData,
    };
    const response = await updateWrapperInfo(
      locationId,
      wrapperBody,
      "Menus Changed",
      "",
      "menu",
    );
    return response;
  };

  const saveProductChanges = async (changedProductData: any) => {
    const productBody = {
      id: selectedProduct[0]?.id,
      locationId: locationId,
      version: selectedProduct[0]?.version,
      ...changedProductData,
    };
    const response = await updateProductInfo(
      locationId,
      productBody,
      "Menus Changed",
      "",
      "menu",
    );
    return response;
  };

  const handleImmediateWrapperValidToggle = async (checked: boolean) => {
    setIsValidMainProduct(checked);
    if (!wrapperData) return;
    setIsLoadingButton(true);
    try {
      const changedData = { valid: checked };
      const wrapperBody = {
        id: wrapperData?.id,
        locationId: locationId,
        version: wrapperData?.version,
        ...changedData,
      };
      const response = await updateWrapperInfo(
        locationId,
        wrapperBody,
        "Menus Changed",
        `Product wrapper valid status changed to ${checked}`,
        "menu",
      );
      if (response?.data?.data) {
        const updatedWrapper = response.data.data;
        setWrapperData(updatedWrapper);
        setSuccess(
          `Product ${checked ? "activated" : "deactivated"} successfully`,
        );
        setTimeout(() => setSuccess(""), 2000);
        if (onProductUpdated) {
          onProductUpdated();
        }
      }
    } catch (err) {
      console.error("Error updating wrapper valid status:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsValidMainProduct(!checked);
    } finally {
      setIsLoadingButton(false);
    }
  };

  const handleImmediateProductValidToggle = async (
    productId: string,
    checked: boolean,
    product: any,
  ) => {
    if (!product) return;
    setIsLoadingButton(true);
    try {
      const productBody = {
        id: product.id,
        locationId: locationId,
        version: product.version,
        valid: checked,
      };
      const response = await updateProductInfo(
        locationId,
        productBody,
        "Menus Changed",
        `Product ${product.name} valid status changed to ${checked}`,
        "menu",
      );
      if (response?.data?.data) {
        const updatedProduct = response.data.data;
        setSelectedProduct((prev) =>
          prev.map((p) =>
            p.id === productId
              ? { ...p, ...updatedProduct, valid: checked }
              : p,
          ),
        );
        setSuccess(
          `Product ${checked ? "activated" : "deactivated"} successfully`,
        );
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (err) {
      console.error("Error updating product valid status:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setIsLoadingButton(false);
    }
  };

  const handleImmediateAvailabilityToggle = async (checked: boolean) => {
    setIsValidProductAvailability(checked);
    if (!wrapperData) return;
    setIsLoadingButton(true);
    try {
      const changedData = { valid: checked };
      const wrapperBody = {
        id: wrapperData?.id,
        locationId: locationId,
        version: wrapperData?.version,
        ...changedData,
      };
      const response = await updateWrapperInfo(
        locationId,
        wrapperBody,
        "Menus Changed",
        `Product wrapper availability changed to ${checked}`,
        "menu",
      );
      if (response?.data?.data) {
        const updatedWrapper = response.data.data;
        setWrapperData(updatedWrapper);
        setSuccess(
          `Availability ${checked ? "enabled" : "disabled"} successfully`,
        );
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (err) {
      console.error("Error updating availability:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsValidProductAvailability(!checked);
    } finally {
      setIsLoadingButton(false);
    }
  };

  const handleImmediateAllYouCanEatToggle = async (checked: boolean) => {
    setAllYouCanEat(checked);
    if (!wrapperData) return;
    setIsLoadingButton(true);
    try {
      const changedData = { allYouCanEat: checked };
      const wrapperBody = {
        id: wrapperData?.id,
        locationId: locationId,
        version: wrapperData?.version,
        ...changedData,
      };
      const response = await updateWrapperInfo(
        locationId,
        wrapperBody,
        "Menus Changed",
        `All You Can Eat setting changed to ${checked}`,
        "menu",
      );
      if (response?.data?.data) {
        const updatedWrapper = response.data.data;
        setWrapperData(updatedWrapper);
        setSuccess(
          `All You Can Eat ${checked ? "enabled" : "disabled"} successfully`,
        );
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (err) {
      console.error("Error updating All You Can Eat:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setAllYouCanEat(!checked);
    } finally {
      setIsLoadingButton(false);
    }
  };

  const handleImmediateKioskToggle = async (checked: boolean) => {
    setIsValidKiosk(checked);
    if (!wrapperData) return;
    setIsLoadingButton(true);
    try {
      const changedData = { kiosk: checked ? "1" : "0" };
      const wrapperBody = {
        id: wrapperData?.id,
        locationId: locationId,
        version: wrapperData?.version,
        ...changedData,
      };
      const response = await updateWrapperInfo(
        locationId,
        wrapperBody,
        "Menus Changed",
        `Kiosk setting changed to ${checked}`,
        "menu",
      );
      if (response?.data?.data) {
        const updatedWrapper = response.data.data;
        setWrapperData(updatedWrapper);
        setSuccess(`Kiosk ${checked ? "enabled" : "disabled"} successfully`);
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (err) {
      console.error("Error updating Kiosk setting:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsValidKiosk(!checked);
    } finally {
      setIsLoadingButton(false);
    }
  };

  const handleSaveProduct = async (data: any) => {
    setIsLoadingButton(true);
    try {
      let wrapperUpdateResponse = null;
      let hasError = false;

      if (hasWrapperChanges || hasTimeWeekChanges) {
        const changedWrapperData = getChangedWrapperData();
        const timeWeekData = {
          weekFrom,
          weekTo,
          timeFrom,
          timeTo,
        };
        const allWrapperChanges = {
          ...(changedWrapperData || {}),
          ...timeWeekData,
        };
        if (Object.keys(allWrapperChanges).length > 0) {
          try {
            wrapperUpdateResponse = await saveProductWrapperChanges(
              allWrapperChanges,
            );
            console.log("Wrapper update response:", wrapperUpdateResponse);
            setHasTimeWeekChanges(false);
          } catch (err) {
            console.error("Error saving product wrapper:", err);
            hasError = true;
            setError("Failed to update product wrapper settings");
          }
        }
      }

      if (hasProductChanges && !hasError) {
        const allChangedProducts = getAllChangedProducts();
        if (allChangedProducts && Object.keys(allChangedProducts).length > 0) {
          try {
            for (const [productId, changes] of Object.entries(
              allChangedProducts,
            )) {
              const product = selectedProduct.find(
                (p) => p.id?.toString() === productId?.toString(),
              );
              if (product && changes && typeof changes === "object") {
                const productBody = {
                  id: product.id,
                  locationId: locationId,
                  version: product.version,
                  ...changes,
                };
                const response = await updateProductInfo(
                  locationId,
                  productBody,
                  "Menus Changed",
                  "",
                  "menu",
                );
                if (response?.data?.data) {
                  setSelectedProduct((prev) =>
                    prev.map((p) =>
                      p.id === productId ? { ...p, ...response.data.data } : p,
                    ),
                  );
                }
              }
            }
          } catch (err) {
            console.error("Error saving products:", err);
            hasError = true;
            setError("Failed to update product details");
          }
        }
      }

      if (!hasWrapperChanges && !hasProductChanges && !hasTimeWeekChanges) {
        setSuccess("No changes to save");
        setTimeout(() => setSuccess(""), 2000);
        setIsLoadingButton(false);
        return;
      }

      if (hasError) {
        setIsLoadingButton(false);
        return;
      }

      if (wrapperUpdateResponse?.data?.data) {
        const updatedWrapper = wrapperUpdateResponse.data.data;
        setWrapperData(updatedWrapper);
        setIsValidMainProduct(updatedWrapper.valid);
        setIsValidKiosk(updatedWrapper.kiosk?.toString() === "1");
        setColor(updatedWrapper.colour || "");
        setAllYouCanEat(updatedWrapper.allYouCanEat || false);
        setIsValidProductAvailability(updatedWrapper.valid);
      }

      resetChanges();
      setSuccess(SUCCESSFULLY_UPDATED);
      if (onProductUpdated) {
        onProductUpdated();
      }
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      console.error("Error saving product:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setIsLoadingButton(false);
    }
  };

  const renderBreadcrumbs = () => {
    const breadcrumbItems = [];

    breadcrumbItems.push(
      <Button
        key="menu"
        size="small"
        onClick={handleNavigateToMenu}
        style={{
          color: theme.palette.custom.orange.contrastText,
          textTransform: "none",
          padding: "4px 8px",
          minWidth: "auto",
        }}
      >
        <HomeIcon style={{ fontSize: "16px", marginRight: "4px" }} />
        {selectedMenuName || "Menu"}
      </Button>,
    );

    if (categoryName) {
      breadcrumbItems.push(
        <Button
          key="category"
          size="small"
          onClick={handleNavigateToCategory}
          style={{
            color:
              directoryStack.length === 0
                ? theme.palette.custom.yellow.main
                : theme.palette.custom.orange.contrastText,
            textTransform: "none",
            padding: "4px 8px",
            minWidth: "auto",
          }}
        >
          {categoryName}
        </Button>,
      );
    }

    directoryStack.forEach((dir, index) => {
      const isLast = index === directoryStack.length - 1;
      breadcrumbItems.push(
        <Button
          key={`dir_${dir.id}`}
          size="small"
          onClick={() => handleNavigateToDirectory(index)}
          style={{
            color: isLast
              ? theme.palette.custom.yellow.main
              : theme.palette.custom.orange.contrastText,
            textTransform: "none",
            padding: "4px 8px",
            minWidth: "auto",
          }}
        >
          {dir.name}
        </Button>,
      );
    });

    breadcrumbItems.push(
      <Button
        key="product"
        size="small"
        style={{
          color: theme.palette.custom.yellow.main,
          textTransform: "none",
          padding: "4px 8px",
          minWidth: "auto",
          cursor: "default",
        }}
      >
        {productData?.name || "Edit Product"}
      </Button>,
    );

    return (
      <Box
        style={{
          padding: "12px 24px",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
              style={{ color: theme.palette.custom.orange.contrastText }}
            />
          }
        >
          {breadcrumbItems}
        </Breadcrumbs>
      </Box>
    );
  };

  const handleCopyText = async (text: string) => {
    if (!text) return;
    try {
      await copyToClipboard(text);
      setSuccess("Copied to clipboard!");
      setTimeout(() => setSuccess(""), 2000);
    } catch {
      setError("Failed to copy text.");
    }
  };

  if (isLoading) {
    return <ProductSkeletonLoading />;
  }

  /* Update availability information - creates the request body with retry on version conflict */
  const handleSubmitData = async () => {
    // Get the current entity ID
    const entityId = `M${productId}`;

    // Get the existing options from menuOptionData
    const existingOptions = menuOptionData?.options || {};

    // Create the updated option object for this entity
    const updatedOption: any = {
      entityId: entityId,
      isActive: isActive,
      availability: availabilityList.availability || [],
    };

    // Delivery Types: If all 3 are selected, send empty array
    if (deliveryTypesList.length === allDeliveryTypes.length) {
      updatedOption.deliveryTypes = [];
    } else {
      updatedOption.deliveryTypes = deliveryTypesList;
    }

    // Always include platform if any selected
    if (platformList.length > 0) {
      if (platformList.length === allPlatformIds.length) {
        updatedOption.platform = [];
      } else {
        updatedOption.platform = platformList;
      }
    } else {
      updatedOption.platform = [];
    }

    // Always include orderChannel if any selected
    if (channelList.length > 0) {
      if (channelList.length === allChannelIds.length) {
        updatedOption.orderChannel = [];
      } else {
        updatedOption.orderChannel = channelList;
      }
    } else {
      updatedOption.orderChannel = [];
    }

    // Merge with existing options
    const updatedOptions = {
      ...existingOptions,
      [entityId]: updatedOption,
    };

    console.log("updatedOptions", entityId, updatedOptions);

    // Prepare the final request body
    const requestBody = {
      id: menuOptionData?.id || "",
      title: menuOptionData?.title || "Default Options",
      primaryLocationId: menuOptionData?.primaryLocationId || "",
      created: menuOptionData?.created || new Date().toISOString(),
      updated: new Date().toISOString().replace("T", " ").slice(0, 19),
      version: menuOptionData?.version || 0,
      options: updatedOptions,
    };

    // Retry function with version update
    const updateWithRetry = async (
      body: any,
      maxRetries: number = 3,
    ): Promise<any> => {
      let currentBody = { ...body };
      let attempts = 0;

      while (attempts < maxRetries) {
        try {
          console.log(`Attempt ${attempts + 1}: Sending update request`);
          const response = await updateMenuOptionInfo(
            match.params.locationId,
            menuOptionData.id,
            currentBody,
          );
          console.log("Update successful:", response);

          // Update local state with response data
          if (response?.data?.data) {
            // Update menuOptionData with new version
            setMenuOptionData(response.data.data);
            // Refresh menu option info
            await getMenuOptionInfo();
            setSuccess("Menu options updated successfully!");
            setTimeout(() => setSuccess(""), 3000);
          }
          return response;
        } catch (error) {
          console.log("Asdasdasfasfsdfsdf", error, menuOptionData)
          const err: any = error as AxiosError;
          // Check if it's a version conflict error (409)
          if (err?.response?.status === 409 || err?.status === 409) {
            attempts++;
            console.log(
              `Version conflict detected. Attempt ${attempts} of ${maxRetries}`,
            );

            if (attempts < maxRetries) {
              try {
                // Fetch the latest data to get the new version
                console.log("Fetching latest data to get new version...");
                const latestRes = await fetchMenuOption(
                  match.params.locationId,
                );
                const latestData = latestRes.data.data;

                // Update menuOptionData with latest data
                setMenuOptionData(latestData);

                // Get the latest options
                const latestOptions = latestData?.options || {};
                const latestEntityOption = latestOptions?.[entityId] || {};

                // Merge user's changes with latest data
                const mergedOptions = {
                  ...latestOptions,
                  [entityId]: {
                    ...latestEntityOption,
                    // Preserve user's changes
                    isActive: isActive,
                    availability: availabilityList.availability || [],
                    deliveryTypes:
                      deliveryTypesList.length === allDeliveryTypes.length
                        ? []
                        : deliveryTypesList,
                    platform:
                      platformList.length === allPlatformIds.length
                        ? []
                        : platformList,
                    orderChannel:
                      channelList.length === allChannelIds.length
                        ? []
                        : channelList,
                  },
                };

                // Update request body with new version
                currentBody = {
                  ...currentBody,
                  version: latestData?.version || 0,
                  options: mergedOptions,
                  updated: new Date()
                    .toISOString()
                    .replace("T", " ")
                    .slice(0, 19),
                };

                console.log(`Retry with new version: ${currentBody.version}`);
              } catch (fetchError) {
                console.error("Error fetching latest data:", fetchError);
                setError(
                  "Failed to fetch latest data. Please refresh and try again.",
                );
                throw fetchError;
              }
            } else {
              // Max retries reached
              setError(
                "Version conflict: Maximum retry attempts reached. Please refresh and try again.",
              );
              throw error;
            }
          } else {
            // Other error
            console.error("Update error:", error);
            setError("Failed to update menu option.");
            throw error;
          }
        }
      }
    };

    try {
      setIsLoadingButton(true);
      await updateWithRetry(requestBody);
    } catch (error) {
      console.error("Final error:", error);
      // Error is already set in the retry function
    } finally {
      setIsLoadingButton(false);
    }
  };
  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>

      <Box
        style={{
          padding: "16px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>{renderBreadcrumbs()}</div>
        <ButtonCommon
          variant="contained"
          onClick={
            isEditDisabled ? handleSubmitData : handleSubmit(handleSaveProduct)
          }
          color="blue"
          style={{
            width: "130px",
            textTransform: "none",
            fontSize: "12px",
          }}
        >
          Save Changes
        </ButtonCommon>
      </Box>

      {isEditDisabled === false && (
        <ProductBanner
          productData={productData}
          productId={productId}
          categoryId={categoryId}
          isValidMainProduct={isValidMainProduct}
          onValidToggle={handleImmediateWrapperValidToggle}
          onCopyText={handleCopyText}
          wrapperData={wrapperData}
          isEditDisabled={isEditDisabled}
        />
      )}

      {isEditDisabled === false && (
        <OverviewSection
          register={register}
          errors={errors}
          open={open}
          setOpen={setOpen}
          itemTypesList={itemTypesList}
          selectedItemType={selectedItemType}
          onItemTypeChange={(e, value) => {
            setSelectedItemType(value);
            trackWrapperChange("itemType", value?.id || value);
          }}
          color={color}
          onColorChange={(newColor) => {
            setColor(newColor);
            trackWrapperChange("colour", newColor);
          }}
          wrapperData={wrapperData}
          productData={productData}
          classes={classes}
          onFieldChange={(field, value, isWrapperField) => {
            if (isWrapperField) {
              trackWrapperChange(field, value);
            } else {
              trackProductChange(field, value);
            }
          }}
        />
      )}

      {isEditDisabled === false && (
        <AvailabilitySection
          isValidProductAvailability={isValidProductAvailability}
          onAvailabilityToggle={handleImmediateAvailabilityToggle}
          allYouCanEat={allYouCanEat}
          onAllYouCanEatToggle={handleImmediateAllYouCanEatToggle}
          isValidKiosk={isValidKiosk}
          onKioskToggle={handleImmediateKioskToggle}
          classes={classes}
          weekFrom={weekFrom}
          setWeekFrom={setWeekFrom}
          weekTo={weekTo}
          setWeekTo={setWeekTo}
          timeFrom={timeFrom}
          setTimeFrom={setTimeFrom}
          timeTo={timeTo}
          setTimeTo={setTimeTo}
          onWeekFromChange={handleWeekFromChange}
          onWeekToChange={handleWeekToChange}
          onTimeFromChange={handleTimeFromChange}
          onTimeToChange={handleTimeToChange}
        />
      )}

      {isEditDisabled === false && (
        <ProductsSection
          selectedProduct={selectedProduct}
          expandedProductIds={expandedProductIds}
          onExpandToggle={toggleProductExpand}
          stockDepartmentSelectedNode={stockDepartmentSelectedNode}
          selectedDepartmentObj={selectedDepartmentObj}
          onDepartmentChange={(dept, productId) => {
            setSelectedDepartmentObj(dept);
            trackProductChange("departmentId", dept?.id, productId);
            setSelectedProduct((prev) =>
              prev.map((p) =>
                p.id === productId ? { ...p, departmentId: dept?.id } : p,
              ),
            );
          }}
          classes={classes}
          calculateMargin={(price, cost) => {
            if (price && cost && price > 0) {
              return (((price - cost) / price) * 100).toFixed(1);
            }
            return "0";
          }}
          onProductFieldChange={(field, value, productId) => {
            trackProductChange(field, value, productId);
            setSelectedProduct((prev) =>
              prev.map((product) => {
                if (product.id === productId) {
                  return { ...product, [field]: value };
                }
                return product;
              }),
            );
          }}
          onImmediateProductValidToggle={handleImmediateProductValidToggle}
          isEditDisabled={isEditDisabled}
        />
      )}

      {isEditDisabled === false && (
        <TagsSpotlightSection
          currentMenuData={currentMenuData}
          selectedTags={selectedTags}
          onTagsChange={(tags) => {
            setSelectedTags(tags);
            trackWrapperChange(
              "tags",
              tags.map((t: any) => t.id),
            );
          }}
          spotlightText={spotlightText}
          onSpotlightTextChange={(text) => {
            setSpotlightText(text);
            trackWrapperChange("spotlight", text);
          }}
          classes={classes}
          wrapperData={wrapperData}
        />
      )}

      {isEditDisabled === false && (
        <SystemInformationSection
          productData={productData}
          wrapperData={wrapperData}
        />
      )}

      {isEditDisabled === false && (
        <ActionButtons
          onCancel={handleBackToDirectory}
          onSave={handleSubmit(handleSaveProduct)}
          isLoading={isLoadingButton}
          isEditDisabled={isEditDisabled}
        />
      )}

      {/* ✅ Updated: Pass menuOptionData to AvailabilitySections */}
      {isEditDisabled && (
        <AvailabilitySections
          menuOptionNodeObj={menuOptionNodeObj}
          menuOptionData={menuOptionData}
          // Pass all states and setters
          isActive={isActive}
          setIsActive={setIsActive}
          deliveryTypesList={deliveryTypesList}
          setDeliveryTypesList={setDeliveryTypesList}
          platformList={platformList}
          setPlatformList={setPlatformList}
          channelList={channelList}
          setChannelList={setChannelList}
          availabilityList={availabilityList}
          setAvailabilityList={setAvailabilityList}
          isActiveInitial={isActiveInitial}
          setIsActiveInitial={setIsActiveInitial}
          deliveryTypesListInitial={deliveryTypesListInitial}
          setDeliveryTypesListInitial={setDeliveryTypesListInitial}
          platformListInitial={platformListInitial}
          setPlatformListInitial={setPlatformListInitial}
          channelListInitial={channelListInitial}
          setChannelListInitial={setChannelListInitial}
          availabilityListInitial={availabilityListInitial}
          setAvailabilityListInitial={setAvailabilityListInitial}
          originalNodeData={originalNodeData}
          setOriginalNodeData={setOriginalNodeData}
        />
      )}

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
      <DefaultAlert
        open={!!success}
        handleClose={() => setSuccess("")}
        message={success}
        severity="success"
      />
    </>
  );
};

export default withAuthority(MenuProduct, Authorities.MENU_READ);
